// tourStagingSession — Pure-TS state machine för onboarding-tour-sandbox.
//
// Varje tour-section får en temporär layer (BlockGroup med collapsed=true +
// isTourSandbox=true) injicerad i workflow-staten. Layer och dess innehåll
// försvinner deterministiskt vid endTourStage — user's riktiga arbete rörs
// aldrig.
//
// Lifecycle:
//   beginTourStage(state, seedKey)  → { stagedState, session }
//                                       session innehåller tempLayerId +
//                                       snapshot av baseState
//   endTourStage(session)           → { state: baseState }  (cleanup)
//   commitTourStage(session)        → { state }            (keep layer,
//                                       flippa isTourSandbox → false)
//
// Robust mot mount/unmount: cleanupStaleTourLayers() kan kallas vid app-
// start för att ta bort övergivna tour-sandboxar från tidigare session.

import type { WorkflowState, WorkflowBlock, BlockGroup, Connector } from '@/types/workflow';
import { getTourSeed, type TourSeedKey } from '@/lib/tourSeedPresets';

export interface TourStagingSession {
  /** Snapshot av state innan tour började. endTourStage återställer till denna. */
  baseState: WorkflowState;
  /** ID på den temporära outer-layern. */
  tempLayerId: string;
  /** IDs på alla nested sub-layers (groups-layers-tour har en). */
  nestedLayerIds: string[];
  /** Seed-key för diagnostik. */
  seedKey: TourSeedKey;
  /** Tidsstämpel när tour-staging började. */
  startedAt: number;
}

export interface BeginResult {
  state: WorkflowState;
  session: TourStagingSession;
}

export interface EndResult {
  state: WorkflowState;
}

/** Beräknar en unik id — injicieras så tester kan vara deterministiska. */
export type IdFn = () => string;

const defaultIdFn: IdFn = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `tour-${Math.random().toString(36).slice(2, 11)}`;

export function beginTourStage(
  state: WorkflowState,
  seedKey: TourSeedKey,
  idFn: IdFn = defaultIdFn,
  now: number = Date.now(),
): BeginResult {
  const seed = getTourSeed(seedKey);

  // Block-IDs som behövs både för connector.from/to och för layer.blockIds.
  const newBlockIds = seed.blocks.map(() => idFn());
  const newBlocks: WorkflowBlock[] = seed.blocks.map((b, i) => ({
    ...b,
    id: newBlockIds[i],
    mtime: now,
  }));

  // Connectors refererar via index.
  const newConnectors: Connector[] = seed.connectors.map(c => ({
    id: idFn(),
    fromId: newBlockIds[c.fromBlockIdx],
    toId: newBlockIds[c.toBlockIdx],
    fromPort: c.fromPort,
    toPort: c.toPort,
    label: c.label,
  }));

  // Nested sub-groups: använd block-indices från seed för att peka ut
  // vilka block-IDs som hör hit. Vi returnerar dess IDs så session
  // kan rensa dem vid end.
  const nestedLayerIds: string[] = [];
  const nestedGroups: BlockGroup[] = (seed.nestedGroups ?? []).map(ng => {
    const id = idFn();
    nestedLayerIds.push(id);
    return {
      id,
      name: ng.name,
      color: ng.color,
      blockIds: ng.blockIndices.map(i => newBlockIds[i]),
      collapsed: ng.collapsed,
      collapsedX: ng.collapsedX,
      collapsedY: ng.collapsedY,
      isTourSandbox: true,
      mtime: now,
    };
  });

  // Outer tour-layer innehåller alla block som INTE är medlemmar av en
  // nested sub-group. (Nested-blocks "flyttar ner" en nivå.)
  const blockIdsInNested = new Set(nestedGroups.flatMap(g => g.blockIds));
  const outerBlockIds = newBlockIds.filter(id => !blockIdsInNested.has(id));

  const tempLayerId = idFn();
  const tempLayer: BlockGroup = {
    id: tempLayerId,
    name: seed.layerName,
    color: seed.color,
    blockIds: outerBlockIds,
    collapsed: true,
    collapsedX: 0,
    collapsedY: 0,
    isTourSandbox: true,
    mtime: now,
  };

  const stagedState: WorkflowState = {
    ...state,
    blocks: [...state.blocks, ...newBlocks],
    connectors: [...state.connectors, ...newConnectors],
    groups: [...state.groups, tempLayer, ...nestedGroups],
  };

  const session: TourStagingSession = {
    baseState: state,
    tempLayerId,
    nestedLayerIds,
    seedKey,
    startedAt: now,
  };

  return { state: stagedState, session };
}

export function endTourStage(session: TourStagingSession): EndResult {
  return { state: session.baseState };
}

/** Commit: keep the sandbox but flip isTourSandbox → false so it stays. */
export function commitTourStage(
  state: WorkflowState,
  session: TourStagingSession,
): EndResult {
  const tourLayerIds = new Set([session.tempLayerId, ...session.nestedLayerIds]);
  const groups = state.groups.map(g =>
    tourLayerIds.has(g.id) ? { ...g, isTourSandbox: false } : g,
  );
  return { state: { ...state, groups } };
}

/**
 * Rensa övergivna tour-sandboxar från tidigare session. Kallas vid app-
 * start / workflow-load. Returnerar ett state där alla
 * `isTourSandbox=true`-grupper och deras blocks/connectors är borta.
 */
export function cleanupStaleTourLayers(state: WorkflowState): WorkflowState {
  const tourGroupIds = new Set(
    state.groups.filter(g => g.isTourSandbox).map(g => g.id),
  );
  if (tourGroupIds.size === 0) return state;

  // Samla alla block-IDs i tour-grupper (inkl. nested).
  const tourBlockIds = new Set<string>();
  for (const g of state.groups) {
    if (tourGroupIds.has(g.id)) {
      for (const bId of g.blockIds) tourBlockIds.add(bId);
    }
  }

  return {
    ...state,
    blocks: state.blocks.filter(b => !tourBlockIds.has(b.id)),
    connectors: state.connectors.filter(
      c => !tourBlockIds.has(c.fromId) && !tourBlockIds.has(c.toId),
    ),
    groups: state.groups.filter(g => !tourGroupIds.has(g.id)),
  };
}

/** Antal tour-sandbox-layers i current state (för diagnostik/tester). */
export function countTourLayers(state: WorkflowState): number {
  return state.groups.filter(g => g.isTourSandbox).length;
}

/** Hitta tour-sandboxens outer-layer om den finns (för auto-navigate). */
export function findTourLayer(state: WorkflowState): BlockGroup | null {
  return state.groups.find(g => g.isTourSandbox && !g.parentGroupId) ?? null;
}

/** Max-ålder innan vi betraktar en tour-sandbox som "stale". */
export const STALE_TOUR_AGE_MS = 24 * 60 * 60 * 1000; // 24h

export function isStaleTourLayer(group: BlockGroup, now: number = Date.now()): boolean {
  if (!group.isTourSandbox) return false;
  if (group.mtime === undefined) return false;
  return now - group.mtime > STALE_TOUR_AGE_MS;
}
